import {
  organizationOnLetter,
  owningBundleThreadSegmentParty,
  threadSegmentParty,
} from '@/lib/medcom/letterDisplay';
import {
  formatMedcomDateTime,
  type BundleTextSegment,
  type ParsedBundleContent,
} from '@/lib/medcom/parseCareCommunicationBundle';
import { sksAdministrativeRoleDisplay } from '@/lib/medcom/sksAdministrativeRole';

export interface SegmentAuthor {
  organizationName?: string | null;
  sorId?: string | null;
  practitionerName?: string | null;
  practitionerRoleCode?: string | null;
  practitionerRoleText?: string | null;
  phone?: string | null;
}

export interface SegmentSignatureView {
  name: string | null;
  role: string | null;
  phone: string | null;
  at: string | null;
  organization: string | null;
}

export interface HeadingLetter {
  topic?: string | null;
  category?: string | null;
  sentAt?: string | null;
  receivedAt?: string | null;
  createdAt?: string | null;
}

function written(value: string | null | undefined): string | null {
  const text = value?.trim() ?? '';
  return text || null;
}

/** Signature box for one thread segment. Role text wins; otherwise the SKS code. */
export function segmentSignature(segment: {
  timestamp?: string | null;
  sender?: SegmentAuthor | null;
}): SegmentSignatureView | null {
  const sender = segment.sender;
  const name = written(sender?.practitionerName);
  const role =
    written(sender?.practitionerRoleText) ??
    sksAdministrativeRoleDisplay(sender?.practitionerRoleCode) ??
    written(sender?.practitionerRoleCode);
  const phone = written(sender?.phone);
  const at = formatMedcomDateTime(segment.timestamp);
  const organization = organizationOnLetter(sender?.organizationName, sender?.sorId);
  if (!name && !role && !phone && !at && !organization) return null;
  return { name, role, phone, at, organization };
}

function headingOf(
  letter: HeadingLetter | null | undefined,
): { topic: string | null; category: string | null } | null {
  const topic = written(letter?.topic);
  const category = written(letter?.category);
  if (!topic && !category) return null;
  return { topic, category };
}

function letterInstant(letter: {
  sentAt?: string | null;
  receivedAt?: string | null;
  createdAt?: string | null;
}): number {
  const iso = letter.sentAt || letter.receivedAt || letter.createdAt || '';
  const time = Date.parse(iso);
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

/**
 * Title and category for the open thread.
 * The letter on screen wins. When that letter has neither, the newest letter that has them is used.
 */
export function threadHeading(input: {
  selected?: HeadingLetter | null;
  selectedLetter?: HeadingLetter | null;
  messages?: readonly HeadingLetter[];
}): { topic: string | null; category: string | null } {
  const fromOpenedLetter = headingOf(input.selectedLetter);
  if (fromOpenedLetter) return fromOpenedLetter;
  const selected = headingOf(input.selected);
  if (selected) return selected;

  let newest: HeadingLetter | undefined;
  let newestTime = Number.NEGATIVE_INFINITY;
  for (const message of input.messages ?? []) {
    if (!headingOf(message)) continue;
    const time = letterInstant(message);
    if (!newest || time >= newestTime) {
      newest = message;
      newestTime = time;
    }
  }
  return headingOf(newest) ?? { topic: null, category: null };
}

/**
 * The letter the thread opens on. A message chosen from a list wins.
 * Otherwise the first received letter, then the first letter.
 */
export function openedThreadMessage<T extends { id: string; direction: string }>(
  messages: readonly T[],
  requestedId: string | null | undefined,
): T | undefined {
  const requested = requestedId?.trim();
  if (requested) {
    const selected = messages.find((message) => message.id === requested);
    if (selected) return selected;
  }
  return messages.find((message) => message.direction === 'in') ?? messages[0];
}

/**
 * Assembled thread segments often have no organisation on a prior author.
 * The opened letter's payload signatures fill the name or SOR so the same
 * lookup as the letter header can run. Each string segment keeps its own text;
 * a missing body is filled from the matching letter payload, not from the
 * opened letter as a whole.
 */
export function fillThreadSegmentSenders<
  T extends {
    kind: string;
    segmentId?: string;
    timestamp?: string;
    text?: string;
    sender?: {
      organizationName?: string;
      sorId?: string;
      practitionerName?: string;
    };
  },
>(segments: readonly T[], letterSegments: BundleTextSegment[] | null | undefined): T[] {
  const letters = letterSegments ?? [];
  const used = new Set<number>();
  return segments.map((segment) => {
    if (segment.kind !== 'string') return segment;
    let next = segment;
    if (segment.sender) {
      const filled = threadSegmentParty(
        segment.sender,
        matchLetterOrganization(segment.sender, segment.timestamp, letters, used),
      );
      next = {
        ...next,
        sender: {
          ...segment.sender,
          organizationName: filled.organizationName ?? undefined,
          sorId: filled.sorId ?? undefined,
        },
      };
    }
    const body = threadSegmentBody(next, matchLetterText(next, letters));
    if (body != null && body !== next.text) {
      next = { ...next, text: body };
    }
    return next;
  });
}

/**
 * Text that belongs to this Beskedsegment. Own assembled text wins. Otherwise
 * the matching letter payload. Line breaks are kept. The opened letter body is
 * not copied onto a different segment.
 */
export function threadSegmentBody(
  segment: {
    kind: string;
    text?: string | null;
  },
  letter: { text?: string | null } | null | undefined,
): string | null {
  if (segment.kind !== 'string') return null;
  const own = typeof segment.text === 'string' ? segment.text : '';
  if (own.trim().length > 0) return own;
  const fromLetter = typeof letter?.text === 'string' ? letter.text : '';
  return fromLetter.trim().length > 0 ? fromLetter : null;
}

function matchLetterText(
  segment: {
    segmentId?: string;
    timestamp?: string;
    sender?: { practitionerName?: string };
  },
  letterSegments: BundleTextSegment[],
): BundleTextSegment | null {
  const segmentId = written(segment.segmentId);
  if (segmentId) {
    const byId = letterSegments.find((item) => item.segmentId === segmentId);
    if (byId) return byId;
  }
  const name = written(segment.sender?.practitionerName);
  const timestamp = written(segment.timestamp);
  if (name && timestamp) {
    const exact = letterSegments.find(
      (item) => item.signature?.name === name && item.timestamp === timestamp,
    );
    if (exact) return exact;
  }
  if (name) {
    const named = letterSegments.filter((item) => item.signature?.name === name);
    if (named.length === 1) return named[0] ?? null;
  }
  return null;
}

function matchLetterOrganization(
  sender: { practitionerName?: string },
  timestampValue: string | undefined,
  letterSegments: BundleTextSegment[],
  used: Set<number>,
): string | null {
  if (letterSegments.length === 0) return null;
  const name = sender.practitionerName?.trim() ?? '';
  const timestamp = timestampValue?.trim() ?? '';
  const byName = letterSegments.findIndex(
    (item, index) => !used.has(index) && Boolean(name) && item.signature?.name === name,
  );
  const byTime = letterSegments.findIndex(
    (item, index) => !used.has(index) && Boolean(timestamp) && item.timestamp === timestamp,
  );
  const byOrder = letterSegments.findIndex((_item, index) => !used.has(index));
  const index = byName >= 0 ? byName : byTime >= 0 ? byTime : byOrder;
  if (index < 0) return null;
  used.add(index);
  return letterSegments[index]?.signature?.organization ?? null;
}

export function careCommunicationThreadPath(
  communicationIdentifier: string,
  messageId?: string | null,
): string {
  const path = `/medcom/letters/${encodeURIComponent(communicationIdentifier)}`;
  const id = messageId?.trim();
  if (!id) return path;
  return `${path}?messageId=${encodeURIComponent(id)}`;
}

export function careCommunicationForwardPath(
  communicationIdentifier: string,
  messageId?: string | null,
): string {
  const path = `/medcom/letters/${encodeURIComponent(communicationIdentifier)}/forward`;
  const id = messageId?.trim();
  if (!id) return path;
  return `${path}?messageId=${encodeURIComponent(id)}`;
}

/**
 * The letter a Beskedsegment belongs to. Communicate may send `messageId`
 * on the segment; otherwise MessageHeader id, letter identifier, or a unique
 * timestamp is matched to a stored thread message.
 */
export type ThreadOwnerMessage = {
  id: string;
  messageHeaderId?: string | null;
  letterIdentifier?: string | null;
  bundleId?: string | null;
  sentAt?: string | null;
  receivedAt?: string | null;
  createdAt?: string | null;
};

export function owningThreadMessageId(
  segment: {
    messageId?: string | null;
    messageHeaderId?: string | null;
    timestamp?: string | null;
    bundleId?: string | null;
  },
  messages: readonly ThreadOwnerMessage[],
): string | null {
  const record = segment as Record<string, unknown>;
  const messageId = written(segment.messageId) ?? writtenString(record.message_id);
  if (messageId) {
    const byId = messages.find((message) => sameMedcomId(message.id, messageId));
    if (byId) return byId.id;
  }

  const header =
    written(segment.messageHeaderId) ?? writtenString(record.message_header_id);
  if (header) {
    const byHeader = messages.find(
      (message) =>
        sameMedcomId(message.messageHeaderId, header) ||
        sameMedcomId(message.letterIdentifier, header) ||
        sameMedcomId(message.id, header),
    );
    if (byHeader) return byHeader.id;
  }

  const bundleId =
    written(segment.bundleId) ??
    writtenString(record.bundleId) ??
    writtenString(record.bundle_id);
  if (bundleId) {
    const byBundle = messages.filter((message) => sameMedcomId(message.bundleId, bundleId));
    if (byBundle.length === 1) return byBundle[0]?.id ?? null;
  }

  const stamp = written(segment.timestamp);
  if (stamp) {
    const matches = messages.filter((message) =>
      [message.sentAt, message.receivedAt, message.createdAt].some((value) =>
        sameInstant(stamp, value),
      ),
    );
    if (matches.length === 1) return matches[0]?.id ?? null;
  }

  return messages.length === 1 ? (messages[0]?.id ?? null) : null;
}

/**
 * Drop assembled segments that belong to an archived Communicate row.
 * Ownership uses message id, MessageHeader id, a bundle id that only the
 * archived row has, or a unique timestamp. Communicate’s thread read still
 * returns archived rows unless it skips archived_at.
 */
export function dropArchivedThreadSegments<
  T extends {
    messageId?: string | null;
    messageHeaderId?: string | null;
    timestamp?: string | null;
    bundleId?: string | null;
  },
>(
  segments: readonly T[],
  allMessages: readonly ThreadOwnerMessage[],
  archivedIds: ReadonlySet<string>,
): T[] {
  if (archivedIds.size === 0) return [...segments];
  const archived = normalizedIdSet(archivedIds);
  const archivedOnlyBundles = exclusiveArchivedBundleIds(allMessages, archivedIds);
  return segments.filter((segment) => {
    const owner = owningThreadMessageId(segment, allMessages);
    if (owner && archived.has(normalizeMedcomId(owner) ?? '')) return false;
    const record = segment as Record<string, unknown>;
    const bundleId =
      written(segment.bundleId) ??
      writtenString(record.bundleId) ??
      writtenString(record.bundle_id);
    const bundleKey = normalizeMedcomId(bundleId);
    if (bundleKey && archivedOnlyBundles.has(bundleKey)) return false;
    return true;
  });
}

function exclusiveArchivedBundleIds(
  allMessages: readonly ThreadOwnerMessage[],
  archivedIds: ReadonlySet<string>,
): Set<string> {
  const archived = normalizedIdSet(archivedIds);
  const activeBundles = new Set<string>();
  const archivedBundles = new Set<string>();
  for (const message of allMessages) {
    const bundleKey = normalizeMedcomId(message.bundleId);
    if (!bundleKey) continue;
    if (archived.has(normalizeMedcomId(message.id) ?? '')) archivedBundles.add(bundleKey);
    else activeBundles.add(bundleKey);
  }
  const only = new Set<string>();
  for (const bundleKey of archivedBundles) {
    if (!activeBundles.has(bundleKey)) only.add(bundleKey);
  }
  return only;
}

function normalizedIdSet(ids: ReadonlySet<string>): Set<string> {
  const set = new Set<string>();
  for (const id of ids) {
    const key = normalizeMedcomId(id);
    if (key) set.add(key);
  }
  return set;
}

/**
 * Organisation (and missing body) from the active letter this segment came
 * from. Archived bundles are never consulted. When the same Organization id
 * is on several active letters, the newest active letter wins.
 */
export function fillThreadSegmentSendersFromActive<
  T extends {
    kind: string;
    segmentId?: string;
    timestamp?: string;
    text?: string;
    messageId?: string | null;
    messageHeaderId?: string | null;
    bundleId?: string | null;
    attachment?: { title?: string };
    sender?: {
      organizationId?: string;
      organizationName?: string;
      sorId?: string;
      practitionerName?: string;
    };
  },
>(
  segments: readonly T[],
  activeMessages: readonly ThreadOwnerMessage[],
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined>,
): T[] {
  const newestFirst = [...activeMessages].sort(
    (left, right) => letterInstant(right) - letterInstant(left),
  );
  const hasLetter = [...letters.values()].some((letter) => Boolean(letter));
  return segments.flatMap((segment) => {
    if (
      hasLetter &&
      !segmentBelongsToActiveLetter(segment, newestFirst, letters)
    ) {
      return [];
    }
    const ownerId =
      owningThreadMessageId(segment, newestFirst) ??
      owningMessageFromActivePayload(segment, newestFirst, letters);
    const ownerLetter = ownerId ? letters.get(ownerId) : undefined;
    const ownerPayload = ownerLetter
      ? matchLetterText(segment, ownerLetter.textSegments)
      : null;
    const organization = organizationFromActiveLetter(
      segment,
      ownerPayload?.signature?.organization ?? null,
      newestFirst,
      letters,
    );
    let next = segment;
    if (segment.sender) {
      const filled = owningBundleThreadSegmentParty(segment.sender, organization);
      next = {
        ...next,
        sender: {
          ...segment.sender,
          organizationName: filled.organizationName ?? undefined,
          sorId: filled.sorId ?? undefined,
        },
      };
    }
    const body = threadSegmentBody(
      next,
      ownerPayload ?? matchLetterTextAcrossActive(segment, newestFirst, letters),
    );
    if (body != null && body !== next.text) {
      next = { ...next, text: body };
    }
    return [next];
  });
}

function segmentBelongsToActiveLetter(
  segment: {
    kind: string;
    segmentId?: string;
    timestamp?: string;
    messageId?: string | null;
    messageHeaderId?: string | null;
    bundleId?: string | null;
    attachment?: { title?: string };
    sender?: { practitionerName?: string };
  },
  activeMessages: readonly ThreadOwnerMessage[],
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined>,
): boolean {
  if (owningThreadMessageId(segment, activeMessages)) return true;
  if (owningMessageFromActivePayload(segment, activeMessages, letters)) return true;
  return false;
}

function owningMessageFromActivePayload(
  segment: {
    kind: string;
    segmentId?: string;
    timestamp?: string;
    attachment?: { title?: string };
    sender?: { practitionerName?: string };
  },
  newestFirst: readonly ThreadOwnerMessage[],
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined>,
): string | null {
  for (const message of newestFirst) {
    const letter = letters.get(message.id);
    if (!letter) continue;
    if (segment.kind !== 'attachment' && matchLetterText(segment, letter.textSegments)) {
      return message.id;
    }
    if (segment.kind === 'attachment' && matchLetterAttachment(segment, letter)) {
      return message.id;
    }
  }
  return null;
}

function matchLetterAttachment(
  segment: { segmentId?: string; attachment?: { title?: string } },
  letter: ParsedBundleContent,
): boolean {
  const segmentId = written(segment.segmentId);
  const title = written(segment.attachment?.title);
  return letter.attachments.some((attachment) => {
    if (segmentId && written(attachment.segmentId) === segmentId) return true;
    if (title && written(attachment.title) === title) return true;
    return false;
  });
}

function matchLetterTextAcrossActive(
  segment: {
    segmentId?: string;
    timestamp?: string;
    sender?: { practitionerName?: string };
  },
  newestFirst: readonly ThreadOwnerMessage[],
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined>,
): BundleTextSegment | null {
  for (const message of newestFirst) {
    const letter = letters.get(message.id);
    if (!letter) continue;
    const match = matchLetterText(segment, letter.textSegments);
    if (match) return match;
  }
  return null;
}

function organizationFromActiveLetter(
  segment: {
    segmentId?: string;
    timestamp?: string;
    sender?: {
      organizationId?: string;
      organizationName?: string;
      sorId?: string;
      practitionerName?: string;
    };
  },
  ownerOrganization: string | null,
  newestFirst: readonly ThreadOwnerMessage[],
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined>,
): string | null {
  if (ownerOrganization) return ownerOrganization;
  const organizationId = written(segment.sender?.organizationId);
  if (organizationId) {
    for (const message of newestFirst) {
      const letter = letters.get(message.id);
      const org = letter?.organizations.find((item) => sameMedcomId(item.id, organizationId));
      if (org?.name) return org.name;
      if (org?.sor) return `SOR ${org.sor}`;
    }
  }
  return matchLetterTextAcrossActive(segment, newestFirst, letters)?.signature?.organization ?? null;
}

function writtenString(value: unknown): string | null {
  return typeof value === 'string' ? written(value) : null;
}

function sameMedcomId(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = normalizeMedcomId(left);
  const b = normalizeMedcomId(right);
  return Boolean(a && b && a === b);
}

function normalizeMedcomId(value: string | null | undefined): string | null {
  const text = written(value);
  return text ? text.replace(/^urn:uuid:/i, '').toLowerCase() : null;
}

function sameInstant(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = written(left);
  const b = written(right);
  if (!a || !b) return false;
  if (a === b) return true;
  const leftMs = Date.parse(a);
  const rightMs = Date.parse(b);
  return !Number.isNaN(leftMs) && leftMs === rightMs;
}
